// Copyright (c) 2026 Michael F. Kamprath
//
// family-apps.js — the shared Ewe-family cross-promo list.
//
// This module is intentionally copy-pasted across the Ewe web apps
// rather than published as an npm package. The list is three rows of
// JSON and the deploy cadence of the apps is independent enough that
// a shared package would add more friction than it removes. When a
// new family app ships (e.g. I See Ewe), update this file in each
// repo and redeploy. The `SELF_ID` constant at the top of each copy
// identifies which app *this* copy runs in, so rendering code can
// filter itself out of its own "also try these" list.
//
// Icon paths are hosted locally under /family/<id>.png so that:
//   - The cross-promo works offline (for read; the tap still opens
//     a new tab that will fail offline, but the tile itself renders).
//   - No external domain dependency is introduced for a feature that
//     runs in the settings drawer of a kids' app.
//   - We are not dependent on the other app's deployment being up at
//     render time.
//
// URLs use https:// production domains. When opened in a new tab they
// go through whatever the user's browser / PWA already knows about.

/** Identifier of the app running *this* copy of the module. */
export const SELF_ID = 'i-hear-ewe';

/**
 * @typedef {Object} FamilyApp
 * @property {string} id        Stable slug; also the filename stem under /family/.
 * @property {string} name      Proper-noun app name. Not translated.
 * @property {string} taglineKey i18n key resolving to a one-line tagline.
 * @property {string} url       Production URL to open in a new tab.
 * @property {string} icon      Absolute path to a square PNG under /family/.
 */

/** @type {ReadonlyArray<FamilyApp>} */
export const FAMILY_APPS = Object.freeze([
  Object.freeze({
    id: 'i-hear-ewe',
    name: 'I Hear Ewe',
    taglineKey: 'family.iHearEwe.tagline',
    url: 'https://ihearewe.com',
    icon: '/family/i-hear-ewe.png',
  }),
  Object.freeze({
    id: 'ewe-can-count',
    name: 'Ewe Can Count',
    taglineKey: 'family.eweCanCount.tagline',
    url: 'https://ewecancount.com',
    icon: '/family/ewe-can-count.png',
  }),
  // I See Ewe will slot in here when it ships.
]);

/**
 * Return the family apps to render in *this* app's settings drawer —
 * every family app except the one running the caller.
 *
 * @returns {ReadonlyArray<FamilyApp>}
 */
export function getOtherFamilyApps() {
  return FAMILY_APPS.filter((app) => app.id !== SELF_ID);
}
